import React, { useState } from "react";
import EmbedCodeModal from "../../molecules/EmbedCodeModal";
import { EmbedSettingsPanel } from "../EmbedSettingsPanel";
import { useEmbedPreviewToken } from "../../../hooks/useEmbedPreviewToken";
import { useStoryShareLinks } from "../../../hooks/useStoryShareLinks";
import { getApiErrorMessage } from "../../../services/apiErrors";
import type { UpdateShareLinkRequest } from "../../../services/shareLinks/shareLinks.types";
import { STORY_EMBED_MODAL_COPY as C } from "./StoryEmbedCodeModal.copy";

export interface StoryEmbedCodeModalProps {
  open: boolean;
  storyId: string;
  onClose: () => void;
}

/** The header's "Copy embed code": the same embed row and settings panel as the Embed tab of Share. */
export const StoryEmbedCodeModal: React.FC<StoryEmbedCodeModalProps> = ({ open, storyId, onClose }) => {
  const { query, put, reset } = useStoryShareLinks(storyId, open);
  const previewToken = useEmbedPreviewToken(storyId, open && Boolean(query.data?.embed));
  const [saveError, setSaveError] = useState<string | null>(null);
  const row = query.data?.embed;
  const code = row?.slug
    ? `<iframe src="${window.location.origin}/e/${row.slug}" width="100%" height="600" frameborder="0" allowfullscreen></iframe>`
    : "";
  const fail = (fallback: string) => ({ onError: (err: unknown) => setSaveError(getApiErrorMessage(err, fallback)) });
  const save = (body: UpdateShareLinkRequest) => {
    setSaveError(null);
    put.mutate({ kind: "embed", body }, fail(C.saveError));
  };
  const loadError = query.isError ? getApiErrorMessage(query.error, C.failed) : null;
  return (
    <EmbedCodeModal
      open={open}
      onClose={onClose}
      size="xl"
      embedCode={code}
      copyDisabledReason={row ? C.copyOff : null}
      isLoading={open && query.isLoading}
      errorMessage={loadError}
      note={saveError ?? (row?.expires_at ? C.expiresNote(new Date(row.expires_at).toLocaleDateString()) : row ? C.neverExpiresNote : null)}
    >
      {row ? (
        <EmbedSettingsPanel
          link={row}
          previewToken={previewToken.data ?? null}
          disabled={put.isPending || reset.isPending}
          onChange={save}
          onRevoke={() => save({ access: "restricted", expires_at: row.expires_at, options: row.options })}
          onReset={() => {
            setSaveError(null);
            reset.mutate("embed", fail(C.resetError));
          }}
        />
      ) : null}
    </EmbedCodeModal>
  );
};

export default StoryEmbedCodeModal;
